import {
  AddedFailureReason,
  ScreeningCorrectionRequest,
  ScreeningCriterion,
} from '@marageti/z4-sdk/lib/travel';
import {
  FailureReasonChip,
  FailureReasonOption,
  ScreeningCriteriaMap,
} from './types';

// These junk/risk reasons can appear if AI flagged them, but Care Team cannot add them from the selector.
const NON_SELECTABLE_FAILURE_REASONS = new Set([
  'HUNTING',
  'DRUGS',
  'SEX_TOURISM',
  'GAMBLING',
]);

export const getFailureReasonLabel = (
  code: string,
  screeningCriteriaMap?: ScreeningCriteriaMap,
) => screeningCriteriaMap?.[code] ?? code;

const toAddedFailureReason = (
  reason: string,
  comment?: string,
): AddedFailureReason => {
  const trimmed = comment?.trim();
  return trimmed ? { reason, comment: trimmed } : { reason };
};

export const commentsByReason = (
  added?: AddedFailureReason[],
): Record<string, string | undefined> => Object.fromEntries(
  (added ?? []).map((item) => [item.reason, item.comment]),
);

export const resolveReasonSet = (
  isLoading: boolean,
  saved: string[] | undefined,
  fallback: string[],
): string[] => (isLoading ? [] : (saved ?? fallback));

export const buildCorrectionPayload = (
  original: string[],
  effective: string[],
  commentsByCode: Record<string, string | undefined> = {},
): ScreeningCorrectionRequest => {
  const originalSet = new Set(original);
  const effectiveSet = new Set(effective);

  return {
    addedFailureReasons: effective
      .filter((code) => !originalSet.has(code) || Boolean(commentsByCode[code]?.trim()))
      .map((code) => toAddedFailureReason(code, commentsByCode[code])),
    removedFailureReasons: original.filter((code) => !effectiveSet.has(code)),
  };
};

export const getSelectableFailureReasons = (
  screeningCriteriaMap: ScreeningCriteriaMap | undefined,
  excludedCodes: string[],
): FailureReasonOption[] => {
  if (!screeningCriteriaMap) {
    return [];
  }

  const excluded = new Set(excludedCodes);

  return Object.entries(screeningCriteriaMap)
    .filter(([code]) => !NON_SELECTABLE_FAILURE_REASONS.has(code) && !excluded.has(code))
    .map(([code, label]) => ({ code, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const toFailureReasonChips = (
  effectiveReasons: string[],
  originalReasons: string[],
  criteriaByName: Record<string, string | undefined>,
  commentsByCode: Record<string, string | undefined> = {},
): FailureReasonChip[] => {
  const originalSet = new Set(originalReasons);

  return effectiveReasons.map((code) => ({
    code,
    isAi: originalSet.has(code),
    tooltip: originalSet.has(code) ? criteriaByName[code] : commentsByCode[code]?.trim() || undefined,
  }));
};

const BUDGET_REASONS = new Set(['MINIMUM_BUDGET', 'MINIMUM_BUDGET_SOLO']);
const BUDGET_AMOUNT_PATTERN = /\$\d/;

export const passedReasonsOf = (
  criteria?: Pick<ScreeningCriterion, 'name' | 'passed'>[] | null,
): string[] => (criteria ?? []).filter((criterion) => criterion.passed).map((criterion) => criterion.name);

// Without an amount, the draft quotes the destination minimum, which a passed budget already meets.
export const needsBudgetAmount = (
  reason: string,
  comment: string | undefined,
  passedReasons: string[],
) => BUDGET_REASONS.has(reason)
  && passedReasons.some((code) => BUDGET_REASONS.has(code))
  && !BUDGET_AMOUNT_PATTERN.test(comment ?? '');
